import React, { useState } from 'react';
import { format, addDays } from 'date-fns';

interface LaunchDateSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onDateSelect: (date: Date) => Promise<void>;
}

const LaunchDateSelector: React.FC<LaunchDateSelectorProps> = ({ isOpen, onClose, onDateSelect }) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      await onDateSelect(selectedDate);
      onClose();
    } catch (err) {
      setError('Failed to set launch date. Please try again.');
      console.error('Error setting launch date:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate the one-year timeline end date
  const endDate = addDays(selectedDate, 365);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-4 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">Set Your Business Launch Date</h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
              disabled={isLoading}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
              {error}
            </div>
          )}
          
          <div className="mb-6">
            <p className="text-gray-600 mb-4">
              Select the date when you plan to officially launch your business. We'll create a personalized
              calendar with all the key tasks you need to complete over the next year.
            </p>
            
            <label htmlFor="launchDate" className="block text-sm font-medium text-gray-700 mb-2">
              Launch Date
            </label>
            <input
              type="date"
              id="launchDate"
              value={format(selectedDate, 'yyyy-MM-dd')}
              onChange={(e) => setSelectedDate(new Date(e.target.value))}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              min={format(new Date(), 'yyyy-MM-dd')}
            />
          </div>
          
          <div className="bg-blue-50 p-3 rounded-md mb-6">
            <h3 className="text-sm font-medium text-blue-800 mb-2">Your 1-Year Business Timeline</h3>
            <p className="text-sm text-blue-700">
              <span className="font-medium">Start:</span> {format(selectedDate, 'MMMM d, yyyy')}
            </p>
            <p className="text-sm text-blue-700">
              <span className="font-medium">End:</span> {format(endDate, 'MMMM d, yyyy')}
            </p>
          </div>
          
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Setting Up Calendar...
                </>
              ) : (
                'Create My Launch Calendar'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LaunchDateSelector;
